import React, { useState } from 'react'
import { Box, Divider, Menu, MenuItem } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import FiberManualRecordOutlinedIcon from '@mui/icons-material/FiberManualRecordOutlined'
import { FS_SM, ICON_SM, ICON_XS, COLOR_PRIMARY, COLOR_PRIMARY_HOVER1 } from '../common/theme'
import { useCloseOnAnchorHidden } from '../common/useCloseOnAnchorHidden'
import type { CfpThreeDotsMenuItem } from '../types'

interface CfpThreeDotsMenuProps {
  items: CfpThreeDotsMenuItem[]
  // Smaller 22px trigger for tight toolbars (default is 26px).
  compact?: boolean
  testId?: string
}

export default function CfpThreeDotsMenu({ items, compact, testId }: CfpThreeDotsMenuProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const open = Boolean(anchor)
  // Auto-close when the trigger drops out of layout — e.g. a tab
  // switch on AppPage swaps in `display: none` on this whole subtree.
  // Without this the menu stays "open" against a hidden anchor and MUI
  // logs "The anchorEl prop provided to the component is invalid".
  useCloseOnAnchorHidden(anchor, () => setAnchor(null))

  const triggerSize = compact ? 22 : 26
  const iconSize = compact ? ICON_XS : ICON_SM

  return (
    <>
      <Box
        data-testid={testId}
        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget as HTMLElement) }}
        sx={{
          width: triggerSize, height: triggerSize,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 999,
          border: 1,
          borderColor: open ? COLOR_PRIMARY : 'transparent',
          bgcolor: open ? COLOR_PRIMARY_HOVER1 : 'transparent',
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'background-color 0.12s, border-color 0.12s',
          '&:hover': { bgcolor: COLOR_PRIMARY_HOVER1, borderColor: COLOR_PRIMARY },
        }}
      >
        <MoreVertIcon sx={{ fontSize: iconSize, opacity: 0.6 }} />
      </Box>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 175,
              borderRadius: 1.5,
              border: 1,
              borderColor: 'divider',
              boxShadow: '0 6px 20px rgba(0,0,0,0.09)',
              py: 0.5,
            },
          },
        }}
      >
        {items.map((item, i) => {
          const hasCheckState = item.checked !== undefined
          const leadIcon = item.icon ?? (
            hasCheckState
              ? (item.checked
                  ? <FiberManualRecordIcon sx={{ fontSize: FS_SM }} />
                  : <FiberManualRecordOutlinedIcon sx={{ fontSize: FS_SM }} />)
              : null
          )
          return (
            <React.Fragment key={item.testId ?? item.label ?? i}>
              {item.dividerAbove && <Divider sx={{ my: 0.5 }} />}
              <MenuItem
                data-testid={item.testId}
                disabled={item.disabled}
                onClick={() => { setAnchor(null); item.onClick() }}
                sx={{
                  fontSize: FS_SM, py: 0.75, px: 1.5, gap: 1,
                  color: item.danger ? 'error.main' : 'text.primary',
                  '&:hover': { bgcolor: item.danger ? 'rgba(220,20,60,0.07)' : COLOR_PRIMARY_HOVER1 },
                  '&.Mui-disabled': { opacity: 0.5 },
                }}
              >
                {leadIcon && (
                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, opacity: item.danger || hasCheckState ? 1 : 0.5 }}>
                    {leadIcon}
                  </Box>
                )}
                {item.label}
                {item.icon && item.checked === true && (
                  <FiberManualRecordIcon sx={{ fontSize: 7, color: 'primary.main', ml: 'auto', pl: 0.5, flexShrink: 0 }} />
                )}
              </MenuItem>
            </React.Fragment>
          )
        })}
      </Menu>
    </>
  )
}
